/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `reportYearSelection` for the Caintra Connect frontend.
 * @module logic/reports/reportYearSelection
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { getBusinessEventsByYear } from "../businessEvents";
import { getB2BEventsByYear } from "../b2bEvents";
import { getVirtualEventsByYear } from "../virtualEvents";

const loadBusinessEventsForYear = async ({
  year,
  navigate,
  setEventsOptions,
  setLoadingEvents,
}) => {
  if (!year) {
    setEventsOptions([]);
    return;
  }

  setLoadingEvents(true);
  try {
    const businessEvents = await getBusinessEventsByYear(year, navigate);
    setEventsOptions(businessEvents);
  } catch (err) {
    logger.error("Error", { err: sanitizeError(err) });
    setEventsOptions([]);
  } finally {
    setLoadingEvents(false);
  }
};

const loadB2bEventsForYear = async ({
  year,
  navigate,
  setB2bEventsOptions,
  setLoadingB2bEvents,
  setSelectedB2bEvent,
}) => {
  setSelectedB2bEvent(null);
  if (!year) {
    setB2bEventsOptions([]);
    return;
  }

  setLoadingB2bEvents(true);
  try {
    const b2bEvents = await getB2BEventsByYear(year, navigate);
    setB2bEventsOptions(b2bEvents);
  } catch (err) {
    logger.error("Error", { err: sanitizeError(err) });
    setB2bEventsOptions([]);
  } finally {
    setLoadingB2bEvents(false);
  }
};

const loadVirtualEventsForYear = async ({
  year,
  navigate,
  setEventsOptions,
  setLoadingEvents,
}) => {
  if (!year) {
    setEventsOptions([]);
    return;
  }

  setLoadingEvents(true);
  try {
    const virtualEvents = await getVirtualEventsByYear(year, navigate);
    setEventsOptions(virtualEvents);
  } catch (err) {
    logger.error("Error", { err: sanitizeError(err) });
    setEventsOptions([]);
  } finally {
    setLoadingEvents(false);
  }
};

/**
 * Loads event options when a report year is selected.
 * @param {number|undefined} reportValue - Selected report option value
 * @param {string|null} year - Selected year
 * @param {Object} deps - State setters and navigation helpers
 */
export const loadEventsForReportYear = async (reportValue, year, deps) => {
  if (reportValue === 6 || reportValue === 7 || reportValue === 9) {
    await loadBusinessEventsForYear({ year, ...deps });
    return;
  }

  if (reportValue === 12) {
    await loadB2bEventsForYear({ year, ...deps });
    return;
  }

  if (reportValue === 13) {
    await loadVirtualEventsForYear({ year, ...deps });
    return;
  }

  // Unsupported report type for year selection. No action required.
};
